import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { XR, Interactive, useXR } from '@react-three/xr';
import { Box, Environment, OrbitControls, Text, Sphere, Torus, Stars, Float, MeshDistortMaterial, RoundedBox } from '@react-three/drei';
import XRSupport from './XRSupport';
import * as THREE from 'three';
import { useXRStore } from '../XRProvider';
import '../canvas.css';

// Floating particles component
const ParticleField = () => {
  const particlesCount = 50;
  const particlePositions = useMemo(() => {
    const positions = [];
    for (let i = 0; i < particlesCount; i++) {
      positions.push([
        (Math.random() - 0.5) * 15,
        Math.random() * 10,
        (Math.random() - 0.5) * 15
      ]);
    }
    return positions;
  }, []);

  return (
    <>
      {particlePositions.map((position, index) => (
        <Sphere 
          key={index} 
          position={position as [number, number, number]} 
          args={[0.05 + Math.random() * 0.1, 8, 8]}
        >
          <meshStandardMaterial 
            color={`hsl(${Math.random() * 360}, 80%, 60%)`}
            emissive={`hsl(${Math.random() * 360}, 80%, 40%)`}
            emissiveIntensity={0.5}
          />
        </Sphere>
      ))}
    </>
  );
};

// Animated shape component
const AnimatedShape = ({ position, color, type }: 
  { position: [number, number, number]; color: string; type: 'sphere' | 'box' | 'torus' }) => {
  const ref = useRef<THREE.Mesh>(null);
  const rotationSpeed = useMemo(() => (
    [Math.random() * 0.01, Math.random() * 0.01, Math.random() * 0.01]
  ), []);
  
  useFrame(() => {
    if (ref.current) {
      ref.current.rotation.x += rotationSpeed[0];
      ref.current.rotation.y += rotationSpeed[1];
      ref.current.rotation.z += rotationSpeed[2];
    }
  });

  return (
    <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
      {type === 'sphere' && (
        <Sphere ref={ref} position={position} args={[0.3, 16, 16]}>
          <MeshDistortMaterial color={color} speed={2} distort={0.3} />
        </Sphere>
      )}
      {type === 'box' && (
        <RoundedBox ref={ref} position={position} args={[0.4, 0.4, 0.4]} radius={0.1}>
          <meshStandardMaterial color={color} metalness={0.5} roughness={0.2} />
        </RoundedBox>
      )}
      {type === 'torus' && (
        <Torus ref={ref} position={position} args={[0.2, 0.1, 16, 32]}>
          <meshStandardMaterial color={color} metalness={0.8} roughness={0.2} />
        </Torus>
      )}
    </Float>
  );
};

// Interactive 3D Text component
const InteractiveText: React.FC<{ 
  position: [number, number, number]; 
  text: string;
  color: string;
  rotation?: [number, number, number];
}> = ({ position, text, color, rotation = [0, 0, 0] }) => {
  const textRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = React.useState(false);
  const [clicked, setClicked] = React.useState(false);
  
  // No rotation animation anymore

  return (
    <Interactive
      onSelect={() => setClicked(!clicked)}
      onHover={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <Text
        ref={textRef}
        position={position}
        rotation={rotation}
        scale={clicked ? 1.2 : 1}
        fontSize={0.5}
        color={hovered ? '#ff0000' : color}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.02}
        outlineColor="#000000"
      >
        {text}
      </Text>
    </Interactive>
  );
};

// Floating text that follows the player
// IMPORTANT: This component can ONLY be used inside a Canvas component!
const FloatingText: React.FC = () => {
  const { player } = useXR();
  const textRef = useRef<THREE.Group>(null);

  useFrame(() => {
    if (player && textRef.current) {
      // Position the text to face the player
      const playerPos = player.position;
      textRef.current.position.set(playerPos.x, playerPos.y + 0.5, playerPos.z - 1.5);
      textRef.current.rotation.copy(player.rotation);
    }
  });

  return (
    <Text
      ref={textRef}
      position={[0, 1.8, -2]}
      fontSize={0.25}
      color="white"
      anchorX="center"
      anchorY="middle"
    >
      Welcome to WebXR!
    </Text>
  );
};

// Main XR Scene component
const XRScene: React.FC = () => {
  // Get XR store from context
  const xrStore = useXRStore();
  
  return (
    <XRSupport>
      <Canvas 
        style={{ 
          width: '100%', 
          height: '100vh', 
          display: 'block'
        }}
        camera={{ position: [0, 1.8, 3], fov: 75 }}
      >
        <XR>
          {/* Environment lighting */}
          <ambientLight intensity={0.5} />
          <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} />
          <pointLight position={[-5, 5, -5]} color="#ff00ff" intensity={0.8} />
          <pointLight position={[5, 5, 5]} color="#00ffff" intensity={0.8} />
          <Environment preset="sunset" />
          
          {/* 3D Text spelling out the requested message */}
          <group position={[0, 2, -3]}>
            <InteractiveText position={[-2.5, 0.7, 0]} text="Hello" color="#4285F4" />
            <InteractiveText position={[-0.5, 0.7, 0]} text="World" color="#EA4335" />
            <InteractiveText position={[1.2, 0, 0]} text="MAH" color="#FBBC05" />
            <InteractiveText position={[3.5, 0, 0]} text="neg" color="#34A853" />
          </group>
          
          {/* Random shapes to make it pop */}
          <AnimatedShape position={[-3, 1, -1.5]} color="#ff5500" type="sphere" />
          <AnimatedShape position={[3, 1, -1.5]} color="#00ff99" type="sphere" />
          <AnimatedShape position={[-2, 3, -2]} color="#ff00ff" type="box" />
          <AnimatedShape position={[2, 3, -2]} color="#ffff00" type="box" />
          <AnimatedShape position={[0, 3.5, -3]} color="#00ffff" type="torus" />
          <AnimatedShape position={[-4, 2, -4]} color="#ff00aa" type="torus" />
          <AnimatedShape position={[4, 2, -4]} color="#00aaff" type="sphere" />
          
          {/* Particle field */}
          <ParticleField />
          
          {/* Background stars */}
          <Stars radius={50} depth={50} count={1000} factor={4} />
          
          <FloatingText />
          
          {/* A larger floor */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
            <planeGeometry args={[20, 20]} />
            <meshStandardMaterial 
              color="#303030" 
              metalness={0.3}
              roughness={0.8}
            />
          </mesh>
        </XR>
        
        {/* Controls for non-VR mode */}
        <OrbitControls minDistance={1} maxDistance={20} />
      </Canvas>
    </XRSupport>
  );
};

export default XRScene; 
